import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ProblemService } from '../../shared/problem.service';
import { MultiChoiceComponent } from '../multi-choice/multi-choice.component';
import { ExerciseService } from '../../exercise.service';
import { ProblemInfo } from '../../shared/ProblemInfo';
import { ParamsService } from '../../../../shared/params.service';
import { Subscription, timer } from 'rxjs';
import { ExerciseModel } from '@generativ/much-smarter-models/exercise/exercise.model';
import { SmartSavers } from '@generativ/much-smarter-models';
import { EntryDictionary, ExerciseType} from '@generativ/ms-student-types';
import { ContentfulService } from '../../../../shared/contentful.service';
import { ContentViewerComponent } from '../../../../shared/content-viewer/content-viewer.component';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap/collapse';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap/tooltip';
import { FormsModule } from '@angular/forms';
import { SpinnerComponent } from '../../../../shared/spinner/spinner.component';
import { TimePipe } from '../../../../shared/pipes/time.pipe';


@Component({
    selector: 'app-problem-multiple-choice-container',
    templateUrl: './problem-multiple-choice-container.component.html',
    styleUrls: ['./problem-multiple-choice-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ContentViewerComponent, NgbCollapse, MultiChoiceComponent, NgbTooltip, FormsModule, SpinnerComponent, TimePipe]
})
export class ProblemMultipleChoiceContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  subscriptions: Subscription = new Subscription();
  lessonId: number;
  exerciseId: number;
  @Input() title: string;
  @Input() isStrategyProblem: boolean;
  @Input() isPersistences: boolean;
  @Input() correctTitle: string;
  @Input() incorrectTitle: string;
  @Input() discardedOptions: string[];
  @Input() nextProblemButton = 'Next problem';
  @Output() onNext: EventEmitter<boolean> = new EventEmitter();
  @Output() onRestartTimer: EventEmitter<boolean> = new EventEmitter();
  @Output() onStopTimer: EventEmitter<boolean> = new EventEmitter();
  content: ExerciseType;

  readyToContinue = false;
  isLoading = false;
  problemInfo: ProblemInfo;
  public hintCollapsed = true;


  // Smart saver used.
  smartSaverUsed: SmartSavers;

  // Boolean for check if flag for review is used or not.
  reviewFlagged = false;

  @ViewChild(MultiChoiceComponent)
  private multiChoiceComponent: MultiChoiceComponent<string>;

  hint: string;
  getHintEnable = true;
  cutIncorrectEnable = true;
  stopTimerEnable = true;
  isPersistence = false;

  // The initial delay time to wait before emitting the first value of `0`. = 10 ms
  // periodOrScheduler 1000ms=1s
  timer = timer(10, 1000);
  subscription: Subscription;
  currentProblemTime = 0;
  currentExerciseTime = 0;

  // Max time per problem
  maxTime = 5 * 60;
  exercisePercentageCompleted = 0;


  constructor(private router: Router,
              private problemService: ProblemService,
              public exerciseService: ExerciseService,
              private paramsService: ParamsService,
              private activatedRoute: ActivatedRoute,
              private contentfulService: ContentfulService) {
  }

  ngOnInit() {
    this.subscriptions.add(this.activatedRoute.params.subscribe((params) => {
      if (params['exerciseId']) {
        this.exerciseId = +params['exerciseId'];
      }
      if (params['lessonId']) {
        this.lessonId = +params['lessonId'];
      }
      this.isPersistence = !!this.paramsService.getRouteDataById('persistence', this.activatedRoute.snapshot);
      this.subscriptions.add(this.exerciseService.startExercise(params['exerciseId'], this.isPersistence)
        .subscribe(() => {
          this.subscriptions.add(this.problemService.getCurrentProblemInfo(this.lessonId).subscribe(
            problemInfo => {
              this.problemInfo = problemInfo;
              if (!this.isStrategyProblem) {
                this.problemService.startProblemTimer(problemInfo.problem.id);
              }
              this.exercisePercentageCompleted = this.exerciseService.getExercisePercentageCompleted();
            }
          ));
      }));
      this.contentfulService.getStudentEntry(EntryDictionary.Exercise.exercise).then(
        (entry) => {
          this.content = new ExerciseType(entry);
        },
      );
    }));
  }

  ngAfterViewInit() {
    this.currentProblemTime = this.problemService.getProblemTimeSpent();
    this.currentExerciseTime = this.exerciseService.getCurrentExerciseTime();
    this.startTimerSubscription();
  }

  // Todo: change all of them to be pipes instead of functions

  get isHintEnable(): boolean {
    return this.getHintEnable && this.exerciseService.getSmartSavers().getHint > 0;
  }

  get isCutIncorrectEnable(): boolean {
    return this.cutIncorrectEnable && this.exerciseService.getSmartSavers().cutIncorrect > 0;
  }

  get isStopTimerEnable(): boolean {
    return this.stopTimerEnable && this.exerciseService.getSmartSavers().stopTimer > 0;
  }

  get numberHints(): number {
    return this.exerciseService.getSmartSavers().getHint;
  }

  get numberCutIncorrect(): number {
    return this.exerciseService.getSmartSavers().cutIncorrect;
  }

  get numberStopTimer(): number {
    return this.exerciseService.getSmartSavers().stopTimer;
  }

  onUserSelection(option: string, skipProblem = false) {
    this.isLoading = true;
    const solutionId = this.problemInfo.problem.solutionId;

    // Stop timer
    this.stopTimer();
    const problemCase = this.paramsService.getRouteDataById('problemCase', this.activatedRoute.snapshot).toString();
    this.subscriptions.add(this.problemService.saveUserAnswer(this.exerciseService.getExerciseProgressId(),
      solutionId,
      option,
      this.isPersistences,
      this.smartSaverUsed,
      this.reviewFlagged).subscribe(
      result => {
        if (problemCase !== 'strategyProblem') {
          this.exerciseService.updateProblemOfExercise();
        }
        if (!skipProblem) {
          this.multiChoiceComponent.setRightOption(result);
          this.readyToContinue = true;
        } else {
          this.loadNextProblem();
        }

        this.isLoading = false;
      }
    ));
  }

  handleReadyToContinue() {
    this.readyToContinue = true;
  }

  loadNextProblem() {
    this.subscriptions.add(this.problemService.getCurrentProblemInfo(this.lessonId).subscribe(
      problemInfo => {
        this.problemInfo = problemInfo;
        this.readyToContinue = false;
        // Initialize all the variables
        this.discardedOptions = null;
        this.reviewFlagged = false;
        this.smartSaverUsed = null;
        this.hint = null;
        this.exercisePercentageCompleted = this.exerciseService.getExercisePercentageCompleted();
        this.multiChoiceComponent.reset();
        if (!this.isStrategyProblem) {
          this.problemService.startProblemTimer(problemInfo.problem.id);
          this.restartTimer();
        }
      }
    ));
  }

  handleNext() {
    this.getHintEnable = this.exerciseService.getSmartSavers().getHint > 0;
    this.cutIncorrectEnable = this.exerciseService.getSmartSavers().cutIncorrect > 0;
    this.stopTimerEnable = this.exerciseService.getSmartSavers().stopTimer > 0;
    if (this.problemInfo.isLastProblem) {
      // Todo: Was this ok????
      // const url = this.isPersistence ? '../conclude' : '../completed';

      this.router.navigate(['../completed'], {relativeTo: this.activatedRoute});
    } else {
      this.loadNextProblem();
    }
  }

  onSkip() {
    this.smartSaverUsed = SmartSavers.Skip;
    this.onUserSelection('', true);
  }

  onCutIncorrect() {
    this.smartSaverUsed = SmartSavers.CutIncorrect;
    this.subscriptions.add(this.exerciseService.getIncorrectOptions().subscribe(
      (discardedOptions) => {
        this.discardedOptions = discardedOptions;
        this.cutIncorrectEnable = false;
      }
    ));
    this.exerciseService.useSmartSaver('cutIncorrect');
  }

  onGetHint() {
    this.smartSaverUsed = SmartSavers.Hint;
    this.subscriptions.add(this.exerciseService.getHint().subscribe(
      (hint) => {
        this.hint = hint;
        this.getHintEnable = false;
      }
    ));
    this.exerciseService.useSmartSaver('getHint');
  }

  /**
   * Use Stop Timer Smart Saver. Stop subscription and disable timer enabled.
   */
  onPauseTimer() {
    this.smartSaverUsed = SmartSavers.StopTimer;
    // Unsubscribe from subscription
    this.subscription.unsubscribe();
    // Set stopTimer smartSaver as not enabled
    this.stopTimerEnable = false;
    // Call problem Service for pausing Problem timer.
    this.problemService.pauseProblemTimer();
    this.exerciseService.useSmartSaver('stopTimer');
  }

  /**
   * In case of faster than expected PACING GOAL we should show on the left part.
   */
  get fasterWidth(): number {
    const currentExercise: ExerciseModel = this.exerciseService.currentExercise;
    const timeSpent = this.secondsToMinutes(this.problemService.getProblemTimeSpent());
    const showFaster = timeSpent < currentExercise.pacingGoal;
    return showFaster ? (timeSpent / currentExercise.pacingGoal) * 100 : 0;
  }

  /**
   * In case of the current time is greater than expected PACING GOAL we should show on the left part.
   */
  get slowerWidth(): number {
    const currentExercise: ExerciseModel = this.exerciseService.currentExercise;
    const timeSpent = this.secondsToMinutes(this.problemService.getProblemTimeSpent());

    const showFaster = (timeSpent > currentExercise.pacingGoal);
    return showFaster ? ((timeSpent - currentExercise.pacingGoal) / (currentExercise.pacingGoal)) * 100 : 0;
  }

  get exerciseProgressWidth(): number {
    const totalProblemCount = this.exerciseService.getCurrentExerciseProblemCount();
    const problemCountPlayed = totalProblemCount - this.exerciseService.problemsPendingLength;
    return (problemCountPlayed / totalProblemCount) * 100;
  }

  private secondsToMinutes(timeInSeconds: number): number {
    return timeInSeconds / 60;
  }

  restartTimer() {
    this.currentProblemTime = this.problemService.getProblemTimeSpent();
    this.subscription.unsubscribe();
    this.startTimerSubscription();
  }

  // Stop timer for.
  private stopTimer() {
    this.problemService.pauseProblemTimer();
    this.subscription.unsubscribe();
  }

  /**
   * Start timer subscription. It will update the variable currentTime each second.
   */
  private startTimerSubscription() {
    this.subscription = this.timer.subscribe(
      () => {
        this.currentProblemTime = this.currentProblemTime + 1;
        this.currentExerciseTime = this.currentExerciseTime + 1;
        // In case of max time.
        if (this.currentProblemTime >= this.maxTime) {
          this.stopTimer();
        }
      }
    );
  }

  ngOnDestroy() {
    this.stopTimer();
    this.subscriptions.unsubscribe();
  }
}
